/**
 * RemediationTab component
 *
 * Displays recommended actions from the incident report
 * and linked Jira tickets.
 */

import { useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, ExternalLink } from "lucide-react";
import type { IncidentReport } from "@/components/security/IncidentReportPanel";
import {
  useLatestActionResult,
  useExecuteAction,
} from "@/hooks/useIssueActions";
import { useIssueTickets } from "@/hooks/useIssueTickets";

interface RemediationTabProps {
  numericIssueId: number;
}

export function RemediationTab({ numericIssueId }: RemediationTabProps) {
  const { data: reportAction, isLoading: reportLoading } =
    useLatestActionResult(numericIssueId, "generate_incident_report");

  const { data: ticketsResponse, isLoading: ticketsLoading } =
    useIssueTickets(numericIssueId);

  const executeAction = useExecuteAction();

  const report = reportAction?.result as { report?: IncidentReport } | null;
  const recommendedActions = report?.report?.ai_narrative?.recommended_actions ?? null;
  const tickets = ticketsResponse?.data ?? [];

  const handleGenerateReport = useCallback(() => {
    executeAction.mutate({
      issueId: numericIssueId,
      data: {
        action_type: "generate_incident_report",
        action_category: "investigation",
      },
    });
  }, [executeAction, numericIssueId]);

  const isGeneratingReport =
    executeAction.isPending &&
    executeAction.variables?.data.action_type === "generate_incident_report";

  return (
    <div className="mt-6 space-y-5">
      {/* Recommended Actions */}
      <section className="rounded-lg border bg-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Recommended Actions
          </h3>
          {recommendedActions && recommendedActions.length > 0 && (
            <span className="text-[10px] text-muted-foreground bg-muted px-2 py-0.5 rounded">
              Auto-generated
            </span>
          )}
        </div>

        {reportLoading || isGeneratingReport ? (
          <div className="flex items-center justify-center py-10 gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {isGeneratingReport ? "Generating..." : "Loading..."}
            </span>
          </div>
        ) : recommendedActions && recommendedActions.length > 0 ? (
          <ol className="space-y-2">
            {recommendedActions.map((action, idx) => (
              <li key={idx} className="flex items-start gap-3 text-sm">
                <span className="flex-shrink-0 text-xs font-medium text-muted-foreground w-5 pt-0.5 text-right tabular-nums">
                  {idx + 1}.
                </span>
                <span className="leading-relaxed">{action}</span>
              </li>
            ))}
          </ol>
        ) : (
          <div className="py-8 text-center">
            <p className="text-sm text-muted-foreground mb-3">
              No recommendations generated yet.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleGenerateReport}
              disabled={isGeneratingReport}
            >
              Generate Recommendations
            </Button>
          </div>
        )}
      </section>

      {/* Linked Tickets */}
      <section className="rounded-lg border bg-card p-5">
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-4">
          Linked Tickets
          {tickets.length > 0 && (
            <span className="ml-2 text-foreground/60">{tickets.length}</span>
          )}
        </h3>

        {ticketsLoading ? (
          <div className="flex items-center justify-center py-10 gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Loading...</span>
          </div>
        ) : tickets.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No tickets linked to this issue.
          </p>
        ) : (
          <div className="rounded-lg border divide-y divide-border">
            {tickets.map((ticket) => (
              <div
                key={ticket.id}
                className="px-3.5 py-2.5 flex items-center gap-3 text-sm"
              >
                <span className="font-mono text-xs font-medium">
                  {ticket.external_ticket_id}
                </span>
                {ticket.summary && (
                  <span className="text-xs text-muted-foreground truncate flex-1 min-w-0">
                    {ticket.summary}
                  </span>
                )}
                {ticket.external_status && (
                  <Badge
                    variant="secondary"
                    className="text-[10px] uppercase px-1.5 py-0 flex-shrink-0"
                  >
                    {ticket.external_status}
                  </Badge>
                )}
                {ticket.provider && (
                  <span className="text-[11px] text-muted-foreground capitalize flex-shrink-0">
                    {ticket.provider}
                  </span>
                )}
                {ticket.external_ticket_url && (
                  <a
                    href={ticket.external_ticket_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                    title={`Open in ${ticket.provider ?? "provider"}`}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
